import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Logo from "../../components/Logo";
import api from "../../lib/api";

const MIN = 10;
const inputCls =
  "mt-2 w-full bg-transparent border-b border-white/20 focus:border-[#e60000] py-3 text-white outline-none transition-colors";
const errorText = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (!err.response) return "The server could not be reached. Please try again in a moment.";
  return typeof detail === "string" ? detail : fallback;
};

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [status, setStatus] = useState(token ? "checking" : "bad"); // checking | ready | bad | done
  const [problem, setProblem] = useState(token ? "" : "This reset link is invalid. Please request a new one.");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Reset password — REGERA";
    if (!token) return;
    api
      .post("/auth/reset-password/check", { token })
      .then(() => setStatus("ready"))
      .catch((err) => {
        setProblem(errorText(err, "This reset link is invalid. Please request a new one."));
        setStatus("bad");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < MIN) return setError(`Use at least ${MIN} characters.`);
    if (password !== confirm) return setError("The two passwords do not match.");
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setStatus("done");
      toast.success("Password updated. Please sign in.");
      setTimeout(() => nav("/admin/login"), 2500);
    } catch (err) {
      const msg = errorText(err, "The password could not be reset. Please try again.");
      if (/link/i.test(msg)) {
        setProblem(msg);
        setStatus("bad");
      } else setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-[#0a0a0a]" data-testid="admin-reset-password">
      <div className="grain-overlay" aria-hidden />
      <div className="w-full max-w-md">
        <Logo size="admin" className="mb-10" />
        <h1 className="font-display text-4xl font-black tracking-tight text-white">Choose a new password</h1>

        {status === "checking" && <p className="mt-6 text-white/60 text-sm">Checking your reset link…</p>}

        {status === "bad" && (
          <div className="mt-6" data-testid="reset-link-problem">
            <p role="alert" className="text-white/85 text-sm leading-relaxed border-l-2 border-[#e60000] pl-4">{problem}</p>
            <p className="mt-4 text-regera-dim text-sm">On the sign-in page, choose “Forgot password?” to get a new link.</p>
            <Link to="/admin/login" className="mt-10 inline-block text-xs tracking-[0.14em] uppercase text-white/60 hover:text-[#e60000]">Back to sign in</Link>
          </div>
        )}

        {status === "done" && (
          <div className="mt-6" data-testid="reset-done">
            <p role="status" className="text-white/85 text-sm border-l-2 border-[#e60000] pl-4">Your password has been reset. Taking you to sign in…</p>
            <Link to="/admin/login" className="mt-10 inline-block text-xs tracking-[0.14em] uppercase text-white/60 hover:text-[#e60000]">Sign in now</Link>
          </div>
        )}

        {status === "ready" && (
          <form onSubmit={submit} noValidate className="mt-10 space-y-8" data-testid="reset-form">
            <label className="block">
              <span className="overline">New password</span>
              <input data-testid="reset-password" type="password" required minLength={MIN} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            </label>
            <label className="block">
              <span className="overline">Confirm password</span>
              <input data-testid="reset-confirm" type="password" required minLength={MIN} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
            </label>
            <p className="text-xs text-white/50">At least {MIN} characters.</p>
            {error && <p role="alert" data-testid="reset-error" className="text-sm text-[#ff4d4d]">{error}</p>}
            <button type="submit" data-testid="reset-submit" disabled={saving} className="w-full bg-[#e60000] text-white py-4 text-sm tracking-[0.14em] uppercase font-medium hover:bg-[#990000] transition-colors disabled:opacity-60">
              {saving ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
